<template>
  <div class="container mt-3">

    <!-- Price Comparison Chart (New) -->
    <div v-if="loaded" class="card chart-card mb-4">
      <div class="card-body">
        <h3>Preisvergleich</h3>
        <p class="text-muted small">Prozentuale Abweichung des aktuellen Preises vom KI-Referenzpreis</p>

        <div class="chart-container">
          <canvas id="priceComparisonChart"></canvas>
        </div>
      </div>
    </div>

    <!-- Innovations Chart -->
    <div class="card chart-card">
      <div class="card-body">
        <h3>Innovations-Effekte Übersicht</h3>

        <div v-if="loaded" class="chart-container">
          <canvas id="innovationChart"></canvas>
        </div>

        <div v-else class="loading-state">
          <p>Daten werden geladen...</p>
        </div>
      </div>
    </div>

    <!-- Marketing Chart (nur wenn Daten vorhanden) -->
    <div v-if="loaded && hasMarketingData" class="card chart-card mt-4">
      <div class="card-body">
        <h3>Marketing-Effekte Übersicht</h3>

        <div class="chart-container">
          <canvas id="marketingChart"></canvas>
        </div>
      </div>
    </div>

    <div class="row mt-4">
      <!-- Capacity Chart -->
      <div v-if="loaded" class="col-md-8">
        <div class="card chart-card h-100">
          <div class="card-body">
            <h3>Kapazitätsauslastung</h3>
            <p class="text-muted small">Verhältnis von verkauften Einheiten zur Gesamtkapazität</p>

            <div class="chart-container">
              <canvas id="capacityChart"></canvas>
            </div>
          </div>
        </div>
      </div>

      <!-- Market Share Pie Chart -->
      <div v-if="loaded" class="col-md-4">
        <div class="card chart-card h-100">
          <div class="card-body">
            <h3>Marktanteile</h3>
            <p class="text-muted small">Verteilung des Marktes auf die Teams</p>

            <div class="chart-container">
              <canvas id="marketShareChart"></canvas>
            </div>
          </div>
        </div>
      </div>

      <!-- Profit Chart -->
      <div v-if="loaded" class="card chart-card mb-4">
        <div class="card-body">
          <h3>Gewinn/Verlust</h3>
          <p class="text-muted small">Gewinn/Verlust</p>

          <div class="chart-container">
            <canvas id="profitChart"></canvas>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js';
import axios from 'axios';
import api from '../js/config/api';

// Eine Liste von vordefinierten Farben für die Teams (ähnlich wie in DepotGraph)
const TEAM_COLORS = [
  "#1c4587", "#c6438b", "#ffe500", "#4cd3de", "#b48af9",
  "#362C28", "#ff8138", "#604895", "#82d9ca", "#ffa600",
  "#00c9f5", "#e467d1", "#ea4a76", "#22baff", "#974696",
  "#73a6ff", "#EDFF86", "#1B998B"
];

export default {
  name: 'BarChart',
  data() {
    return {
      priceComparisonChart: null,
      innovationChart: null,
      marketingChart: null,
      capacityChart: null,
      marketShareChart: null,
      loaded: false,
      hasMarketingData: false,
    };
  },
  async mounted() {
    await this.fetchData();
  },
  methods: {
    async fetchData() {
      try {
        const response = await axios.get(`${api.getUrl()}/api/frontend/bar-graph/`);
        const data = response.data;

        if (data.dataLoaded) {
          this.checkMarketingData(data);
          this.loaded = true;

          this.$nextTick(() => {
            this.renderPriceComparisonChart(data);
            this.renderProfitChart(data);
            this.renderInnovationChart(data);
            if (this.hasMarketingData) {
              this.renderMarketingChart(data);
            }
            this.renderCapacityChart(data);
            this.renderMarketShareChart(data);
          });
        } else {
          console.warn('Keine Daten geladen (Seminar existiert nicht oder ist inaktive).');
        }
      } catch (error) {
        console.error('Fehler beim Laden der Graph-Daten:', error);
      }
    },
    checkMarketingData(serverData) {
      const teamIds = Object.keys(serverData.graphData);
      this.hasMarketingData = teamIds.some(id => {
        const teamData = serverData.graphData[id];
        return (teamData.first_marketing_effect > 0 ||
          teamData.second_marketing_effect > 0 ||
          teamData.third_marketing_effect > 0);
      });
    },
    getTeamColors(count) {
      const colors = [];
      for (let i = 0; i < count; i++) {
        if (i < TEAM_COLORS.length) {
          colors.push(TEAM_COLORS[i]);
        } else {
          // Fallback: Zufällige Farbe generieren wenn mehr Teams als vordefinierte Farben
          const randomColor = '#' + Math.floor(Math.random()*16777215).toString(16);
          colors.push(randomColor);
        }
      }
      return colors;
    },

    renderPriceComparisonChart(serverData) {
      const ctx = document.getElementById('priceComparisonChart').getContext('2d');
      const teamIds = Object.keys(serverData.teamNames);
      const labels = teamIds.map(id => serverData.teamNames[id]);

      // Berechnung der prozentualen Abweichung
      const priceDifferences = teamIds.map(id => {
        const current = serverData.graphData[id].current_price;
        const ai = serverData.graphData[id].ai_price;
        if (ai === 0) return 0;
        return (current / ai) * 100;
      });

      const bgColors = priceDifferences.map(val => val >= 0 ? 'rgba(54, 162, 235, 0.6)' : 'rgba(255, 99, 132, 0.6)');
      const borderColors = priceDifferences.map(val => val >= 0 ? 'rgba(54, 162, 235, 1)' : 'rgba(255, 99, 132, 1)');

      this.priceComparisonChart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: labels,
          datasets: [{
            label: 'Preisabweichung in %',
            data: priceDifferences,
            backgroundColor: bgColors,
            borderColor: borderColors,
            borderWidth: 1
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { display: false },
          scales: {
            yAxes: [{
              ticks: {
                beginAtZero: true,
                callback: function(value) {
                  return value + '%';
                }
              },
            }]
          },
          annotation: {
            annotations: [{
              type: 'line',
              mode: 'horizontal',
              scaleID: 'y-axis-0',
              value: 100,
              borderColor: 'red',
              borderWidth: 2,
              borderDash: [5, 5],
              label: {
                enabled: true,
                content: 'Vorgeschlagener Preis',
                position: 'right',
                backgroundColor: 'rgba(255, 0, 0, 0.7)'
              }
            }]
          },
          tooltips: {
            callbacks: {
              label: function(tooltipItem) {
                return tooltipItem.yLabel.toFixed(2) + '%';
              },
            }
          }
        }
      });
    },

    renderProfitChart(serverData) {
      const ctx = document.getElementById('profitChart').getContext('2d');
      const teamIds = Object.keys(serverData.teamNames);
      const labels = teamIds.map(id => serverData.teamNames[id]);

      // Berechnung des Gewinns im Verhältnis zum KI-Preis
      const profitRatios = teamIds.map(id => {
        const profit = serverData.graphData[id].profit || 0;
        const ai_price = serverData.graphData[id].ai_price;
        const ai_max_market = serverData.graphData[id].ai_max_market;
        if (!ai_price || ai_price === 0) return 0;
        if (!ai_max_market || ai_max_market === 0) return 0;
        return 100 * profit / (ai_price * ai_max_market);
      });

      // Grün für Gewinn, Rot für Verlust
      const bgColors = profitRatios.map(val => val >= 0 ? 'rgba(40, 167, 69, 0.6)' : 'rgba(220, 53, 69, 0.6)');
      const borderColors = profitRatios.map(val => val >= 0 ? 'rgba(40, 167, 69, 1)' : 'rgba(220, 53, 69, 1)');

      this.profitChart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: labels,
          datasets: [{
            label: 'Gewinn/Verlust',
            data: profitRatios,
            backgroundColor: bgColors,
            borderColor: borderColors,
            borderWidth: 1
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: {display: false},
          scales: {
            yAxes: [{
              ticks: {
                display: false,
                beginAtZero: true,
              },
              gridLines: {
                drawBorder: false
              }
            }],
            xAxes: [{
              gridLines: {
                display: false
              }
            }]
          },
          tooltips: {
            enabled: false // Keine Zahlen beim Hover anzeigen
          }
        }
      });
    },


    renderInnovationChart(serverData) {
      const ctx = document.getElementById('innovationChart').getContext('2d');
      const teamIds = Object.keys(serverData.teamNames);
      const labels = teamIds.map(id => serverData.teamNames[id]);

      const firstEffects = teamIds.map(id => serverData.graphData[id].first_innovation_effect);
      const secondEffects = teamIds.map(id => serverData.graphData[id].second_innovation_effect);
      const thirdEffects = teamIds.map(id => serverData.graphData[id].third_innovation_effect);
      const lastPeriodEffect = teamIds.map(id => (serverData.graphData[id].innovation_last_period/4)/1.25)

      const innoDatasets = [];

      // Nur anzeigen, wenn mindestens ein Team einen Effekt aus der letzten Runde hat
      if (lastPeriodEffect.some(val => val !== 0)) {
        innoDatasets.push({
          label: 'Letzte Runde',
          data: lastPeriodEffect,
          backgroundColor: '#808080',
        });
      }

      innoDatasets.push(
        {
          label: 'Funktionalität',
          data: firstEffects,
          backgroundColor: '#4CAF50',
        },
        {
          label: 'Benutzererlebnis',
          data: secondEffects,
          backgroundColor: '#36A2EB',
        },
        {
          label: 'Qualität',
          data: thirdEffects,
          backgroundColor: '#FFCE56',
        }
      );

      this.innovationChart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: labels,
          datasets: innoDatasets
        },
        options: {
         ...this.getChartOptions(),
          annotation: {
            annotations: [{
              type: 'line',
              mode: 'horizontal',
              scaleID: 'y-axis-0',
              value: 0,
              borderColor: 'black',
              borderWidth: 1,
              label: {
                enabled: false,
              }
            }]
          }

        }
      });
    },
    renderMarketingChart(serverData) {
      const ctx = document.getElementById('marketingChart').getContext('2d');
      const teamIds = Object.keys(serverData.teamNames);
      const labels = teamIds.map(id => serverData.teamNames[id]);

      const firstEffects = teamIds.map(id => serverData.graphData[id].first_marketing_effect);
      const secondEffects = teamIds.map(id => serverData.graphData[id].second_marketing_effect);
      const thirdEffects = teamIds.map(id => serverData.graphData[id].third_marketing_effect);
      const lastPeriodEffect = teamIds.map(id => (serverData.graphData[id].marketing_last_period/4)/1.25)

      const marketingDatasets = [];
      const hasLastPeriodData = lastPeriodEffect.some(val => val !== 0);

      // Nur anzeigen, wenn mindestens ein Team einen Effekt aus der letzten Runde hat
      if (hasLastPeriodData) {
        marketingDatasets.push({
          label: 'Letzte Runde',
          data: lastPeriodEffect,
          backgroundColor: '#808080',
        });
      }

      marketingDatasets.push(
        {
          label: 'Marketing 1',
          data: firstEffects,
          backgroundColor: '#FF6384',
        },
        {
          label: 'Marketing 2',
          data: secondEffects,
          backgroundColor: '#9966FF',
        },
        {
          label: 'Marketing 3',
          data: thirdEffects,
          backgroundColor: '#FF9F40',
        },
      );

      this.marketingChart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: labels,
          datasets: marketingDatasets
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: {
            display: false,
          },
          annotation: {
            annotations: [{
              type: 'line',
              mode: 'horizontal',
              scaleID: 'y-axis-0',
              value: 0,
              borderColor: 'black',
              borderWidth: 1,
              label: {
                enabled: false,
              }
            }]
          },
          tooltips: {
            mode: 'index',
            intersect: false,
            callbacks: {
              label: function(tooltipItem) {
                const index = tooltipItem.index;
                const datasetIndex = tooltipItem.datasetIndex;
                const teamId = teamIds[index];
                const teamData = serverData.graphData[teamId];
                const value = tooltipItem.yLabel;

                const offset = hasLastPeriodData ? 1 : 0;
                if (hasLastPeriodData && datasetIndex === 0) {
                  return `Letzte Runde: ${value}`;
                }

                let channelName = '';
                if (datasetIndex === 0 + offset) {
                  channelName = teamData.first_marketing_channel || 'Unbekannt';
                } else if (datasetIndex === 1 + offset) {
                  channelName = teamData.second_marketing_channel || 'Unbekannt';
                } else if (datasetIndex === 2 + offset) {
                  channelName = teamData.third_marketing_channel || 'Unbekannt';
                }

                // Anzeigeformat: "Kanalname: Wert"
                return `${channelName}: ${value}`;
              }
            }
          },
          scales: {
            xAxes: [{
              stacked: true,
              ticks: {
                autoSkip: false
              }
            }],
            yAxes: [{
              stacked: true,
              ticks: {
                beginAtZero: true
              }
            }]
          }
        },
      });
    },
    renderCapacityChart(serverData) {
      const ctx = document.getElementById('capacityChart').getContext('2d');
      const teamIds = Object.keys(serverData.teamNames);
      const labels = teamIds.map(id => serverData.teamNames[id]);

      const marketSharePercentage = teamIds.map(id => {
        const d = serverData.graphData[id];
        const marketShare = d.market_share || 1;
        return marketShare * 100;
      });

      const overCapacityPercentage = teamIds.map(id => {
        const d = serverData.graphData[id];
        const over = d.over_capacity || 0;
        return over * 100;
      });

      const underCapacityPercentage = teamIds.map(id => {
        const d = serverData.graphData[id];
        const under = d.under_capacity || 0;
        return under * 100;
      });

      this.capacityChart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: labels,
          datasets: [
            {
              label: 'Verkaufte Einheiten',
              data: marketSharePercentage,
              backgroundColor: '#28a745',
            },
            {
              label: 'Überkapazität',
              data: overCapacityPercentage,
              backgroundColor: '#36A2EB',
              borderWidth: 1
            },
            {
              label: 'Unterkapazität',
              data: underCapacityPercentage,
              backgroundColor: '#dc3545',
              borderWidth: 1
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: {
            position: 'top',
          },
          tooltips: {
            mode: 'index',
            intersect: false,
            callbacks: {
              label: function(tooltipItem, data) {
                return data.datasets[tooltipItem.datasetIndex].label + ': ' + tooltipItem.yLabel.toFixed(2) + '%';
              },
            }
          },
          scales: {
            xAxes: [{
              stacked: true,
              ticks: {
                autoSkip: false
              }
            }],
            yAxes: [{
              stacked: true,
              ticks: {
                beginAtZero: true,
                callback: function(value) {
                  return value + '%';
                }
              },
            }]
          },
        }
      });
    },
    renderMarketShareChart(serverData) {
      const ctx = document.getElementById('marketShareChart').getContext('2d');
      const teamIds = Object.keys(serverData.teamNames);

      const labels = teamIds.map(id => serverData.teamNames[id]);
      const marketShares = teamIds.map(id => serverData.graphData[id].market_share * 100);

      const backgroundColors = this.getTeamColors(teamIds.length);

      const totalShare = marketShares.reduce((a, b) => a + b, 0);

      if (totalShare < 99.9) {
        const remaining = 100 - totalShare;
        marketShares.push(remaining);
        labels.push('Marktpotenzial (offen)');
        backgroundColors.push('#e0e0e0');
      }

      this.marketShareChart = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: labels,
          datasets: [{
            data: marketShares,
            backgroundColor: backgroundColors,
            borderWidth: 1
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: {
            position: 'bottom',
            labels: {
              boxWidth: 12,
              filter: function(item) {
                return item.text !== 'Marktpotenzial (offen)';
              }
            }
          },
          tooltips: {
            callbacks: {
              label: function(tooltipItem, data) {
                const dataset = data.datasets[tooltipItem.datasetIndex];
                const currentValue = dataset.data[tooltipItem.index];
                const label = data.labels[tooltipItem.index];
                return `${label}: ${Math.round(currentValue * 100) / 100}%`;
              }
            }
          }
        }
      });
    },
    getChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          position: 'top',
        },
        tooltips: {
          mode: 'index',
          intersect: false,
          callbacks: {
            footer: (tooltipItems, data) => {
              const isCapacityChart = data.datasets.some(d => d.label === 'Ungenutzte Kapazität');
              if (isCapacityChart) {
                let sum = 0;
                tooltipItems.forEach(function(tooltipItem) {
                  sum += data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
                });
                return 'Gesamtkapazität: ' + sum;
              }
            }
          }
        },
        scales: {
          xAxes: [{
            stacked: true,
            ticks: {
              autoSkip: false
            }
          }],
          yAxes: [{
            stacked: true,
            ticks: {
              beginAtZero: true
            }
          }]
        },
      };
    }
  },
};
</script>

<style scoped>
.chart-card {
  background: #fff;
  border: 1px solid #b5b5b5;
  border-radius: 5px;
  box-shadow: 0 0 11px -5px rgba(0,0,0,0.75);
  margin-bottom: 2em;
}

.card-body {
  padding: 20px;
}

.chart-container {
  position: relative;
  height: 50vh;
  width: 100%;
}

.loading-state {
  text-align: center;
  padding: 2em;
  font-style: italic;
  color: #666;
}
</style>
